import * as Sentry from '@sentry/browser';
import { onError } from 'apollo-link-error';
import { fromPromise } from 'apollo-link';
import { apiEndpoints } from 'helpers/endpoints';
import { AsyncStorage, getApolloClient, apolloPersistor, analyticsPersistor } from 'services';
import { trackError, errorMessage, trackInfo, infoMessage, saveTokens, appStage } from 'helpers';
import axios from 'axios';
import axiosRetry from 'axios-retry';

const customAxiosClient = axios.create();

axiosRetry(customAxiosClient, {
  retries: 200,
  retryCondition: (error) => {
    return (
      axiosRetry.isNetworkError(error) ||
      error.code === 'ECONNABORTED' ||
      error?.response?.status === 0 ||
      error?.response?.status !== 400
    );
  },
  retryDelay: () => {
    return 5000;
  },
});

let isRefreshing = false;
let pendingRequests: any = [];

// Reload only needs to be true if using function outside of context (eg in this apollo link)
export async function executeLogout(reloadRequired: boolean) {
  trackInfo(infoMessage.Logout);
  // Sentry shutdown and draining
  if (appStage !== 'local') {
    await Sentry.close(5000);
  }
  // Clear apollo
  await getApolloClient().then((apollo) => {
    return apollo.clearStore();
  });
  await apolloPersistor.purge();
  await analyticsPersistor.purge();
  // Clear storage
  await AsyncStorage.clear();
  // Reloads and redirects to root
  if (reloadRequired) {
    window.location.reload();
  }
  return;
}

const resolvePendingRequests = () => {
  pendingRequests.map((callback: any) => callback());
  pendingRequests = [];
};

async function getNewToken() {
  const refreshToken = await AsyncStorage.get('refreshToken');
  return new Promise((resolve, reject) => {
    if (!refreshToken) {
      reject();
    }

    customAxiosClient
      .post(
        apiEndpoints.tokenRefresh,
        { refresh_token: refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .then((response) => {
        trackInfo(infoMessage.TokenRefreshSuccessful);
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function handleFailure(errorMessage: string, errorPayload: any, shouldLogout: boolean) {
  trackError(errorMessage, errorPayload);
  pendingRequests = [];
  if (shouldLogout) {
    executeLogout(true);
  }
}

function handleParralel(resolve: any) {
  pendingRequests.push(() => resolve());
}

function handleFinish() {
  isRefreshing = false;
}

export const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      switch (err.extensions?.code) {
        case 'INVALID-TOKEN':
          let forward$;

          if (!isRefreshing) {
            isRefreshing = true;
            trackInfo(infoMessage.TokenRefreshInitiated);
            forward$ = fromPromise(
              getNewToken()
                .then((response: any) => {
                  saveTokens(response.data.access_token, response.data.refresh_token)
                    .then(() => {
                      resolvePendingRequests();
                      return response.access_token;
                    })
                    .catch((error) => {
                      handleFailure(errorMessage.FailedToSaveTokens, error, false);
                    });
                })
                .catch((error) => {
                  if (error?.response?.status === 400) {
                    handleFailure(errorMessage.TokenRefreshRejected, error.response, true);
                  } else {
                    handleFailure(
                      errorMessage.TokenRefreshFailure,
                      error?.response ? error.response : error,
                      false
                    );
                  }
                })
                .finally(() => {
                  handleFinish();
                })
            ).filter((value) => Boolean(value));
          }

          trackInfo(infoMessage.TokenRefreshInProgress);
          forward$ = fromPromise(
            new Promise((resolve) => {
              handleParralel(resolve);
            })
          );

          return forward$.flatMap(() => forward(operation));

        case 'UNAUTHENTICATED':
          const revokedAccessPayload = {
            operation: operation.operationName,
            code: err.extensions?.code,
            message: err.message,
          };
          trackError(errorMessage.UnauthenticatedCodeFromServer, revokedAccessPayload);
          break;

        default:
          const ShareNotEnabledOnFeedLevelError =
            operation.operationName.includes('Feed') &&
            err.message.includes('Sharing not enabled for node');
          if (!ShareNotEnabledOnFeedLevelError) {
            const graphQLError = {
              operation: operation.operationName,
              code: err.extensions?.code,
              message: err.message,
            };
            trackError(errorMessage.GraphQLError, graphQLError);
          }
      }
    }
  }
  if (networkError) {
    const payload = {
      operation: operation.operationName,
      message: 'Failed to fetch',
    };
    trackError(errorMessage.NetworkError, payload);
  }
});
